import type {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  VoidFunctionComponent,
} from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioButton from '@mui/material/Radio';
import RadioButtonGroup from '@mui/material/RadioGroup';
import * as _ from 'lodash';

import Flex from '../../generic/Flex';
import Text from '../../generic/Text';
import type { CVSSRule } from './types';
import Select from '../../generic/Select/Select';
import type { SecurityRuleCVSSFilter, SecurityRuleLevel } from '../../../../shared/model-typings';

type CVSSFilterComponentProps = {
  cvssRule: CVSSRule;
  setCvssRule: Dispatch<SetStateAction<CVSSRule>>;
  setChanged: Dispatch<SetStateAction<boolean>>;
};

type SeverityCardProps = {
  cvssRule: CVSSRule;
  onChangeMinimumCVSSLevel: (e: { value: string; label: string } | null) => void;
};

type ScoreCardProps = {
  cvssRule: CVSSRule;
  onChangeMinimumCVSSValue: (e: { value: number; label: string } | null) => void;
};

const CVSS_SCORE_RANGE = _.range(11);
const CVSS_SCORE_OPTIONS = CVSS_SCORE_RANGE.map((value) => ({
  value,
  label: value.toString(),
}));

const CVSS_LEVEL_OPTIONS = [
  {
    value: 'LOW',
    label: 'Low',
  },
  {
    value: 'MEDIUM',
    label: 'Medium',
  },
  {
    value: 'HIGH',
    label: 'High',
  },
  {
    value: 'CRITICAL',
    label: 'Critical',
  },
];

const CVSSCard: FunctionComponent = ({ children }) => (
  <div
    className="card cvss-card"
    style={{ background: 'rgba(223, 223, 223, 0.2)' }}
  >
    <Flex justify="flex-start">{children}</Flex>
  </div>
);

const SeverityCard: VoidFunctionComponent<SeverityCardProps> = ({
  cvssRule,
  onChangeMinimumCVSSLevel,
}) => (
  <CVSSCard>
    <p>Any vulnerabilities with a CVSS severity of</p>
    <div style={{ margin: 'auto 10px', width: '150px' }}>
      <Select
        options={CVSS_LEVEL_OPTIONS}
        onChange={onChangeMinimumCVSSLevel}
        value={CVSS_LEVEL_OPTIONS.find((x) => x.value === cvssRule.minimumCVSSLevel) ?? CVSS_LEVEL_OPTIONS[0]}
      />
    </div>
    <p>or higher will be flagged as an issue</p>
  </CVSSCard>
);

const ScoreCard: VoidFunctionComponent<ScoreCardProps> = ({
  cvssRule,
  onChangeMinimumCVSSValue,
}) => (
  <CVSSCard>
    <p>Any vulnerabilities with a CVSS score of</p>
    <div style={{ margin: 'auto 10px', width: '95px' }}>
      <Select
        options={CVSS_SCORE_OPTIONS}
        value={CVSS_SCORE_OPTIONS.find((score) => score.value === cvssRule.minimumCVSSValue)}
        onChange={onChangeMinimumCVSSValue}
      />
    </div>
    <p>or higher will be flagged as an issue</p>
  </CVSSCard>
);

const CVSSFilterComponent: VoidFunctionComponent<CVSSFilterComponentProps> = ({
  cvssRule,
  setCvssRule,
  setChanged: setHasChanged,
}) => {
  return (
    <div className="card">
      <div className="row">
        <div className="col-md-3">
          <Text.H3>Severity Filter</Text.H3>
          <p>
            Define which kinds of vulnerabilities should be flagged by this
            policy.
            <br />
            Note that issues with unknown severity information will always be
            included.
          </p>
        </div>
        <div className="col-md-9">
          <div style={{ marginTop: '32px', marginBottom: '25px' }}>
            <span className="margin-bottom-md">
              <Text.H4>Filter By</Text.H4>
            </span>
            <RadioButtonGroup
              name="cvssFilterGroup"
              style={{ display: 'inline-flex', width: '40%' }}
              defaultValue="LEVEL"
              value={cvssRule.cvssFilter}
              onChange={(e, filterVal: SecurityRuleCVSSFilter) => {
                setCvssRule({
                  ...cvssRule,
                  cvssFilter: filterVal,
                });
                setHasChanged(true);
              }}
            >
              <FormControlLabel
                label="CVSS Severity"
                value="LEVEL"
                control={<RadioButton />}
              />
              <FormControlLabel
                label="CVSS Score"
                value="VALUE"
                control={<RadioButton />}
              />
            </RadioButtonGroup>
          </div>
          {cvssRule.cvssFilter === 'LEVEL' ? (
            <SeverityCard
              cvssRule={cvssRule}
              onChangeMinimumCVSSLevel={(e) => {
                if (e) {
                  setCvssRule({
                    ...cvssRule,
                    minimumCVSSLevel: e.value as SecurityRuleLevel,
                  });
                  setHasChanged(true);
                }
              }}
            />
          ) : (
            <ScoreCard
              cvssRule={cvssRule}
              onChangeMinimumCVSSValue={(e) => {
                if (e) {
                  setCvssRule({
                    ...cvssRule,
                    minimumCVSSValue: Number(e.value),
                  });
                  setHasChanged(true);
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CVSSFilterComponent;
